import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { FiEdit3, FiFileText, FiMessageSquare, FiSearch } from "react-icons/fi";
import Message from "./message";
import Input from "./Input";
import "../styles/chat.css";

const TOKEN = "Bearer 3St9p0fmzKBnldM3ta859V5GvSGfJQAPrCmXt19N8C99czAufkgfJQQJ99CEACGhslBAArohAAABAZBSbRdr.DiTI2psVW6vgIPcsAmb5ktEQt31fQYWXYmUNapuj20Tv9OQ3ONJyJQQJ99CEACGhslBAArohAAABAZBS2FmQ";

const suggestions = [
    { icon: FiFileText, text: "Resuma os principais pontos de um documento" },
    { icon: FiSearch, text: "Ajude a localizar uma informação jurídica" },
    { icon: FiMessageSquare, text: "Explique um tema jurídico de forma simples" },
];

export default function Chat({
    messages,
    setMessages,
    conversationId,
    setConversationId,
    conversationKey,
    onNewConversation,
}) {
    const { accounts } = useMsal();
    const user = accounts[0];
    const name = user?.name?.split(" - ")[0] || "Usuário";
    const [isConnecting, setIsConnecting] = useState(true);
    const [connectionError, setConnectionError] = useState("");
    const messagesEndRef = useRef(null);
    const wsRef = useRef(null);
    const seenActivityIdsRef = useRef(new Set());

    const streamBotMessage = useCallback((text) => {
        const id = `bot-${Date.now()}-${Math.random()}`;
        setMessages((current) => [...current, { id, text: "", from: "bot" }]);

        let position = 0;
        const revealNextChunk = () => {
            position = Math.min(position + 24, text.length);
            setMessages((current) => current.map((message) => (
                message.id === id ? { ...message, text: text.slice(0, position) } : message
            )));
            if (position < text.length) window.setTimeout(revealNextChunk, position < 120 ? 18 : 0);
        };
        revealNextChunk();
    }, [setMessages]);

    const connectWebSocket = useCallback((streamUrl) => {
        if (wsRef.current) wsRef.current.close();
        const socket = new WebSocket(streamUrl);
        wsRef.current = socket;

        socket.onmessage = (event) => {
            if (!event.data) return;
            try {
                const payload = JSON.parse(event.data);
                (payload.activities || []).forEach((activity) => {
                    const isBotMessage = activity.from?.id === "Bartolomeu-chat" && activity.type === "message";
                    const wasSeen = activity.id && seenActivityIdsRef.current.has(activity.id);
                    if (isBotMessage && !wasSeen) {
                        if (activity.id) seenActivityIdsRef.current.add(activity.id);
                        streamBotMessage(activity.text || "");
                    }
                });
            } catch (error) {
                console.warn("Mensagem inválida recebida do assistente:", error);
            }
        };
        socket.onclose = () => {
            if (wsRef.current === socket) wsRef.current = null;
        };
        socket.onerror = () => {
            if (wsRef.current === socket) wsRef.current = null;
        };
    }, [streamBotMessage]);

    useEffect(() => {
        let cancelled = false;
        setIsConnecting(true);
        setConnectionError("");
        seenActivityIdsRef.current.clear();
        if (wsRef.current) wsRef.current.close();

        async function startConversation() {
            try {
                const response = await fetch(
                    "https://directline.botframework.com/v3/directline/conversations",
                    { method: "POST", headers: { Authorization: TOKEN } },
                );
                if (!response.ok) throw new Error("Falha ao iniciar conversa");
                const data = await response.json();
                if (cancelled) return;
                setConversationId(data.conversationId);
                connectWebSocket(data.streamUrl);
            } catch (error) {
                if (!cancelled) {
                    console.error("Erro ao iniciar conversa:", error);
                    setConnectionError("Não foi possível iniciar a conversa. Tente novamente.");
                }
            } finally {
                if (!cancelled) setIsConnecting(false);
            }
        }

        startConversation();
        return () => {
            cancelled = true;
            if (wsRef.current) wsRef.current.close();
        };
    }, [conversationKey, connectWebSocket, setConversationId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const reconnectWebSocket = async () => {
        if (!conversationId) throw new Error("Conversa ainda não está pronta");
        const response = await fetch(
            `https://directline.botframework.com/v3/directline/conversations/${conversationId}`,
            { headers: { Authorization: TOKEN } },
        );
        if (!response.ok) throw new Error("Falha ao reconectar");
        const data = await response.json();
        connectWebSocket(data.streamUrl);
    };

    const sendMessage = async (text, fileUrl) => {
        if (!conversationId) return;
        setConnectionError("");
        setMessages((current) => [
            ...current,
            { id: `user-${Date.now()}`, text, image: fileUrl, from: "user" },
        ]);

        if (!wsRef.current || wsRef.current.readyState > WebSocket.OPEN) {
            await reconnectWebSocket();
        }

        const response = await fetch(
            `https://directline.botframework.com/v3/directline/conversations/${conversationId}/activities`,
            {
                method: "POST",
                headers: { Authorization: TOKEN, "Content-Type": "application/json" },
                body: JSON.stringify({
                    type: "message",
                    text: text || "Arquivo enviado. Faça um detalhamento do conteúdo.",
                    from: { id: user?.localAccountId || "usuario" },
                    attachments: fileUrl ? [{ contentType: "application/octet-stream", contentUrl: fileUrl }] : [],
                }),
            },
        );
        if (!response.ok) throw new Error("Não foi possível enviar a mensagem");
    };

    const hasStarted = messages.length > 0;

    return (
        <main className="chat-container">
            <header className="mobile-header">
                <strong>Bartolomeu</strong>
                <button onClick={onNewConversation} aria-label="Nova conversa"><FiEdit3 /></button>
            </header>

            {hasStarted ? (
                <section className="messages" aria-live="polite">
                    <div className="messages-inner">
                        {messages.map((message, index) => (
                            <div className={`message-row ${message.from}`} key={message.id || index}>
                                {message.from === "bot" && (
                                    <div className="assistant-avatar"><img src="/ICONE_PNG.png" alt="" /></div>
                                )}
                                <Message msg={message} />
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                </section>
            ) : (
                <section className="welcome">
                    <div className="welcome-icon"><img src="/ICONE_PNG.png" alt="" /></div>
                    <h1>Olá, {name}. Como posso ajudar?</h1>
                    <p>Sou o Bartolomeu, assistente jurídico da Fontes & Advogados Associados.</p>
                    <div className="suggestions">
                        {suggestions.map(({ icon: Icon, text }) => (
                            <button key={text} onClick={() => sendMessage(text, null)} disabled={isConnecting}>
                                <Icon aria-hidden="true" />
                                <span>{text}</span>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {connectionError && <div className="connection-error" role="alert">{connectionError}</div>}
            <Input onSend={sendMessage} isConnecting={isConnecting} />
        </main>
    );
}
